/**
 * Login page.
 *
 * Two phases:
 *  1. Email + password → api.login. On success, redirect to the originating
 *     route (or `/`).
 *  2. If the returned user has `mustChange: true`, show a "set a new password"
 *     prompt that calls api.changePassword, then refresh + redirect.
 *
 * "Sign in with Google" is a plain link to /api/auth/google/start — the API runs the
 * whole Google round-trip and comes back with the session cookie set, or to
 * /login?google=<reason> when it refuses (see api/src/functions/google-auth.ts).
 *
 * Already-authenticated users are bounced straight to `/`.
 */
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, KeyRound, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

interface LocationState {
  from?: { pathname: string; search?: string };
}

// Why the API sent the person back here from Google (api/src/shared/google.ts reasons).
const GOOGLE_REFUSALS: Record<string, string> = {
  not_registered:
    "That Google account doesn't have a wiki account. Ask Dan to add you, or sign in with your email and password.",
  domain: 'Use your @settheroom.com Google account.',
  email_unverified: 'Google has not verified that email address.',
  cancelled: 'Google sign-in was cancelled.',
  expired: 'The Google sign-in took too long. Please try again.',
  not_configured: 'Google sign-in is not available yet. Use your email and password.',
};

// Google returns to ONE fixed address, so start there even when this page was opened on
// the old *.azurestaticapps.net alias (its cookies would not reach the callback).
const GOOGLE_START = window.location.hostname.endsWith('.azurestaticapps.net')
  ? 'https://wiki.settheroomstudio.com/api/auth/google/start'
  : '/api/auth/google/start';

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function Login() {
  const { user, loading, login, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from;
  const dest = from ? from.pathname + (from.search ?? '') : '/';
  const [params] = useSearchParams();
  const googleRefusal = params.get('google');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [mustChange, setMustChange] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    googleRefusal ? GOOGLE_REFUSALS[googleRefusal] ?? 'Google sign-in failed. Please try again.' : null,
  );

  // Already signed in and nothing to change → leave the login page.
  if (!loading && user && !user.mustChange && !mustChange) {
    return <Navigate to={dest} replace />;
  }

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const me = await login(email, password);
      if (me.mustChange) {
        setCurrentPassword(password);
        setMustChange(true);
      } else {
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Incorrect email or password.'
          : err instanceof Error
            ? err.message
            : 'Sign in failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      await refresh();
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-2 p-4">
      <div className="card w-full max-w-sm p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <BookOpen size={22} className="text-accent" />
          <span className="text-lg font-semibold">STR Wiki</span>
        </div>

        {!mustChange ? (
          <>
          <a
            href={`${GOOGLE_START}?next=${encodeURIComponent(dest)}`}
            className="btn btn-outline w-full justify-center"
          >
            <GoogleIcon />
            Sign in with Google
          </a>
          <div className="my-4 flex items-center gap-3 text-xs text-ink-faint">
            <span className="h-px flex-1 bg-border" />
            or with your password
            <span className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={onLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-xs font-medium text-ink-muted">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@settheroom.com"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-xs font-medium text-ink-muted"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button type="submit" disabled={busy} className="btn btn-primary w-full">
              {busy && <Loader2 size={15} className="animate-spin" />}
              Sign in
            </button>
          </form>
          <p className="mt-4 text-center text-xs text-ink-muted">
            <Link to="/forgot" className="text-accent hover:underline">
              Forgot your password?
            </Link>
          </p>
          </>
        ) : (
          <form onSubmit={onChangePassword} className="space-y-4">
            <div className="flex items-start gap-2 rounded bg-surface-3 p-3 text-sm text-ink-muted">
              <KeyRound size={16} className="mt-0.5 flex-shrink-0" />
              <p>You must set a new password before continuing.</p>
            </div>
            <div>
              <label
                htmlFor="newPassword"
                className="mb-1 block text-xs font-medium text-ink-muted"
              >
                New password
              </label>
              <input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                required
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="mb-1 block text-xs font-medium text-ink-muted"
              >
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Re-enter new password"
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button type="submit" disabled={busy} className="btn btn-primary w-full">
              {busy && <Loader2 size={15} className="animate-spin" />}
              Set password &amp; continue
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
